import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { TrendingDown, TrendingUp, Target, MessageCircle, Clock, Lock, Download } from 'lucide-react';
import { toast } from 'sonner';

interface WeeklyReportCardProps {
  relationshipId: string;
  tier: 'free' | 'premium' | 'pro';
}

interface WeeklyReport {
  weekStart: string;
  weekEnd: string;
  averageRTI: number;
  previousWeekRTI: number;
  trend: 'improving' | 'worsening' | 'stable';
  trendChange: number;
  primaryFocus: string;
  focusCount: number;
  initiationBalance: { you: number; them: number };
  previousInitiation: { you: number; them: number };
  insights: string[];
  checkinCount: number;
  nextWeekForecast?: number;
}

export function WeeklyReportCard({ relationshipId, tier }: WeeklyReportCardProps) {
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [showReport, setShowReport] = useState(false);

  const isPremium = tier !== 'free';
  const isPro = tier === 'pro';

  useEffect(() => {
    if (isPremium) {
      generateWeeklyReport();
    }
  }, [relationshipId, isPremium]);

  const generateWeeklyReport = () => {
    const allCheckins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    const checkins = allCheckins.filter((c: any) => c.relationshipId === relationshipId);

    if (checkins.length < 2) {
      return;
    }

    // Get this week's data (last 7 days)
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - 7);
    
    const thisWeek = checkins.filter((c: any) => 
      new Date(c.timestamp) >= weekStart
    );

    // Get previous week's data (8-14 days ago)
    const previousWeekStart = new Date(now);
    previousWeekStart.setDate(previousWeekStart.getDate() - 14);
    const previousWeekEnd = new Date(now);
    previousWeekEnd.setDate(previousWeekEnd.getDate() - 7);

    const previousWeek = checkins.filter((c: any) => {
      const date = new Date(c.timestamp);
      return date >= previousWeekStart && date < previousWeekEnd;
    });

    if (thisWeek.length === 0) {
      return;
    }

    // Calculate averages
    const averageRTI = Math.round(
      thisWeek.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / thisWeek.length
    );

    const previousWeekRTI = previousWeek.length > 0
      ? Math.round(previousWeek.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / previousWeek.length)
      : averageRTI;

    const trendChange = previousWeekRTI - averageRTI;
    const trend = trendChange > 5 ? 'improving' : trendChange < -5 ? 'worsening' : 'stable';

    // Determine primary focus from action cards
    const focuses = thisWeek.map((c: any) => {
      const score = c.rtiScore || 0;
      if (score > 60) return 'Restraint';
      if (score > 35) return 'Reconnection';
      return 'Stability';
    });

    const focusCounts = focuses.reduce((acc: any, f: string) => {
      acc[f] = (acc[f] || 0) + 1;
      return acc;
    }, {});

    const primaryFocus = Object.entries(focusCounts)
      .sort(([, a]: any, [, b]: any) => b - a)[0][0] as string;
    const focusCount = focusCounts[primaryFocus];

    // Calculate initiation balance
    const avgInitiation = Math.round(
      thisWeek.reduce((sum: number, c: any) => sum + (c.components?.initiation || 50), 0) / thisWeek.length
    );

    const prevAvgInitiation = previousWeek.length > 0
      ? Math.round(previousWeek.reduce((sum: number, c: any) => sum + (c.components?.initiation || 50), 0) / previousWeek.length)
      : avgInitiation;

    const initiationBalance = {
      you: avgInitiation,
      them: 100 - avgInitiation,
    };

    const previousInitiation = {
      you: prevAvgInitiation,
      them: 100 - prevAvgInitiation,
    };

    // Generate insights
    const insights: string[] = [];

    if (trendChange > 10) {
      insights.push(`RTI improved by ${trendChange} points - your strategies are working`);
    } else if (trendChange < -10) {
      insights.push(`RTI increased by ${Math.abs(trendChange)} points - review your approach`);
    }

    if (avgInitiation > 70) {
      insights.push('High pursuit detected - consider strategic restraint');
    } else if (avgInitiation < 40 && previousInitiation.you > 60) {
      insights.push('Reduced pursuit is creating space for them to initiate');
    }

    // Check for time-based patterns
    const morningCheckins = thisWeek.filter((c: any) => {
      const hour = new Date(c.timestamp).getHours();
      return hour >= 6 && hour < 12;
    });

    const eveningCheckins = thisWeek.filter((c: any) => {
      const hour = new Date(c.timestamp).getHours();
      return hour >= 18 && hour <= 23;
    });

    if (morningCheckins.length > 0 && eveningCheckins.length > 0) {
      const morningAvg = morningCheckins.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / morningCheckins.length;
      const eveningAvg = eveningCheckins.reduce((sum: number, c: any) => sum + (c.rtiScore || 0), 0) / eveningCheckins.length;
      
      if (eveningAvg > morningAvg + 10) {
        insights.push(`Evening check-ins show ${Math.round(eveningAvg - morningAvg)}% higher RTI - anxiety increases at night`);
      }
    }

    // Restraint correlation
    const restraintHistory = JSON.parse(localStorage.getItem('tension_restraint_history') || '[]');
    const thisWeekRestraint = restraintHistory.filter((r: any) => {
      const start = new Date(r.startTime);
      return start >= weekStart;
    });

    if (thisWeekRestraint.length >= 2 && trendChange > 0) {
      insights.push(`${thisWeekRestraint.length} restraint sessions preceded RTI drops`);
    }

    // Pro: Next week forecast
    let nextWeekForecast: number | undefined;
    if (isPro && thisWeek.length >= 5) {
      // Simple linear regression forecast
      const trend = trendChange / 7; // Daily change
      nextWeekForecast = Math.max(0, Math.min(100, Math.round(averageRTI - (trend * 7))));
    }

    setReport({
      weekStart: weekStart.toLocaleDateString(),
      weekEnd: now.toLocaleDateString(),
      averageRTI,
      previousWeekRTI,
      trend,
      trendChange,
      primaryFocus,
      focusCount,
      initiationBalance,
      previousInitiation,
      insights,
      checkinCount: thisWeek.length,
      nextWeekForecast,
    });
  };

  const downloadReport = () => {
    if (!report) return;

    const reportText = `
TENSION Weekly Report
Generated: ${new Date().toLocaleDateString()}
Period: ${report.weekStart} - ${report.weekEnd}

SUMMARY
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Average RTI: ${report.averageRTI}
Previous Week: ${report.previousWeekRTI}
Change: ${report.trendChange > 0 ? '+' : ''}${report.trendChange} points (${report.trend})

Check-ins Completed: ${report.checkinCount}/7

PRIMARY FOCUS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${report.primaryFocus} (appeared in ${report.focusCount} action cards)

INITIATION BALANCE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
You: ${report.initiationBalance.you}%
Them: ${report.initiationBalance.them}%
Previous Week: ${report.previousInitiation.you}% / ${report.previousInitiation.them}%

INSIGHTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${report.insights.map((i, idx) => `${idx + 1}. ${i}`).join('\n')}

${report.nextWeekForecast ? `\nNEXT WEEK FORECAST\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\nPredicted RTI: ${report.nextWeekForecast}` : ''}
    `;

    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tension-weekly-report-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    toast.success('Report downloaded');
  };

  if (!isPremium) {
    return (
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Lock className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900 mb-1">Weekly Report Card</h3>
            <p className="text-sm text-neutral-600 mb-3">
              Get comprehensive weekly summaries with trends, insights, and pattern analysis delivered every Sunday.
            </p>
            <Button
              size="sm"
              onClick={() => window.location.href = '/settings'}
              className="gap-2"
            >
              Unlock with Premium
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card className="p-6 bg-neutral-50">
        <p className="text-sm text-neutral-600 text-center">
          Complete at least 2 check-ins this week to generate your weekly report
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-neutral-900">Weekly Report Card</h3>
            <p className="text-xs text-neutral-500">
              {report.weekStart} - {report.weekEnd}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowReport(!showReport)}
          >
            {showReport ? 'Hide' : 'View'} Details
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/60 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              {report.trend === 'improving' ? (
                <TrendingDown className="w-4 h-4 text-green-600" />
              ) : report.trend === 'worsening' ? (
                <TrendingUp className="w-4 h-4 text-red-600" />
              ) : (
                <Target className="w-4 h-4 text-yellow-600" />
              )}
              <span className="text-xs text-neutral-600">This Week</span>
            </div>
            <div className="text-2xl font-light text-neutral-900">{report.averageRTI}</div>
            <div className={`text-xs ${
              report.trend === 'improving' ? 'text-green-600' : 
              report.trend === 'worsening' ? 'text-red-600' : 
              'text-yellow-600'
            }`}>
              {report.trendChange > 0 ? '↓' : report.trendChange < 0 ? '↑' : '→'} {Math.abs(report.trendChange)} from last week
            </div>
          </div>

          <div className="bg-white/60 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-neutral-600">Your Focus</span>
            </div>
            <div className="text-lg font-medium text-neutral-900">{report.primaryFocus}</div>
            <div className="text-xs text-neutral-600">
              {report.focusCount} of {report.checkinCount} days
            </div>
          </div>
        </div>

        {showReport && (
          <>
            {/* Initiation Balance */}
            <div className="bg-white/60 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-neutral-900">Initiation Balance</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-600">You</span>
                  <span className="font-medium text-neutral-900">{report.initiationBalance.you}%</span>
                </div>
                <div className="bg-neutral-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-400 to-pink-400 h-2 rounded-full"
                    style={{ width: `${report.initiationBalance.you}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-neutral-500">
                  <span>Last week: {report.previousInitiation.you}%</span>
                  <span className={
                    report.initiationBalance.you < report.previousInitiation.you
                      ? 'text-green-600'
                      : report.initiationBalance.you > report.previousInitiation.you
                      ? 'text-orange-600'
                      : 'text-neutral-500'
                  }>
                    {report.initiationBalance.you < report.previousInitiation.you && '✓ Better balance'}
                    {report.initiationBalance.you > report.previousInitiation.you && 'More pursuit'}
                    {report.initiationBalance.you === report.previousInitiation.you && 'No change'}
                  </span>
                </div>
              </div>
            </div>

            {/* Insights */}
            {report.insights.length > 0 && (
              <div className="bg-white/60 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-neutral-900">This Week's Insights</span>
                </div>
                <ul className="space-y-2">
                  {report.insights.map((insight, idx) => (
                    <li key={idx} className="text-sm text-neutral-700 flex items-start gap-2">
                      <span className="text-blue-600 mt-0.5">•</span>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pro: Next Week Forecast */}
            {isPro && report.nextWeekForecast !== undefined && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">PRO</span>
                  <span className="text-sm font-medium text-purple-900">Next Week Forecast</span>
                </div>
                <div className="text-2xl font-light text-purple-900">{report.nextWeekForecast}</div>
                <p className="text-xs text-purple-700 mt-1">
                  Predicted RTI based on current trajectory
                </p>
              </div>
            )}

            {/* Download Button */}
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={downloadReport}
            >
              <Download className="w-4 h-4" />
              Download Full Report
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
